import {
    SectionFooter,
    SectionBody,
} from '@/components/molecules/PremiumOverview'
import { Header } from './Header'
import { useTranslation } from 'react-i18next'
import escEnergySourcingAndContractsUpload from '/assets/esc-premium-upload.png'
import escHowItWorks from '/assets/esc-premium-how-it-works.png'
import escGhgEmissionsReport from '/assets/esc-premium-ghg-emissions-report.png'
import { Badge, Icon } from '@/components/atoms'
import { FormattedI18nString } from '@/utils/FormattedI18nString'
import { TFunction } from 'i18next'

const badgeClassName = 'h-6 w-6 !rounded-[50%]'

const boldClassName = 'text-body-medium font-bold leading-6 capitalize'

const checkClassName = 'text-linkPrimary'

const createSections = (t: TFunction) => [
    {
        id: 'upload-your-energy-sourcing-and-contracts',
        imageProps: {
            fgSrc: escEnergySourcingAndContractsUpload,
            className: 'max-h-[408px]',
        },
        textProps: {
            title: t('data_management.premiumOverview.firstSection.title'),
            subtitle: t(
                'data_management.premiumOverview.firstSection.subtitle'
            ),
            list: [
                {
                    icon: <Icon name="check" className={checkClassName} />,
                    text: (
                        <FormattedI18nString
                            boldClassName={boldClassName}
                            i18nKey="data_management.premiumOverview.firstSection.firstListItem"
                        />
                    ),
                    textClassName: 'text-body-small',
                },
                {
                    icon: <Icon name="check" className={checkClassName} />,
                    text: (
                        <FormattedI18nString
                            boldClassName={boldClassName}
                            i18nKey="data_management.premiumOverview.firstSection.secondListItem"
                        />
                    ),
                    textClassName: 'text-body-small',
                },
                {
                    icon: <Icon name="check" className={checkClassName} />,
                    text: (
                        <FormattedI18nString
                            boldClassName={boldClassName}
                            i18nKey="data_management.premiumOverview.firstSection.thirdListItem"
                        />
                    ),
                    textClassName: 'text-body-small',
                },
            ],
        },
        reverse: false,
    },
    {
        id: 'how-it-works',
        imageProps: { fgSrc: escHowItWorks, className: 'max-h-[408px]' },
        textProps: {
            title: t('data_management.premiumOverview.secondSection.title'),
            list: [
                {
                    icon: (
                        <Badge variant="linkPrimary" className={badgeClassName}>
                            1
                        </Badge>
                    ),
                    text: (
                        <FormattedI18nString i18nKey="data_management.premiumOverview.secondSection.firstListItem" />
                    ),
                },
                {
                    icon: (
                        <Badge variant="linkPrimary" className={badgeClassName}>
                            2
                        </Badge>
                    ),
                    text: (
                        <FormattedI18nString i18nKey="data_management.premiumOverview.secondSection.secondListItem" />
                    ),
                },
                {
                    icon: (
                        <Badge variant="linkPrimary" className={badgeClassName}>
                            3
                        </Badge>
                    ),
                    text: (
                        <FormattedI18nString i18nKey="data_management.premiumOverview.secondSection.thirdListItem" />
                    ),
                },
            ],
        },
        reverse: true,
    },
    {
        id: 'ghg-emissions-report',
        imageProps: {
            fgSrc: escGhgEmissionsReport,
            className: 'max-h-[408px]',
        },
        textProps: {
            title: t('data_management.premiumOverview.thirdSection.title'),
            subtitle: t(
                'data_management.premiumOverview.thirdSection.subtitle'
            ),
            list: [
                {
                    icon: <Icon name="check" className={checkClassName} />,
                    text: (
                        <FormattedI18nString i18nKey="data_management.premiumOverview.thirdSection.firstListItem" />
                    ),
                },
                {
                    icon: <Icon name="check" className={checkClassName} />,
                    text: (
                        <FormattedI18nString i18nKey="data_management.premiumOverview.thirdSection.secondListItem" />
                    ),
                },
                {
                    icon: <Icon name="check" className={checkClassName} />,
                    text: (
                        <FormattedI18nString i18nKey="data_management.premiumOverview.thirdSection.thirdListItem" />
                    ),
                },
            ],
        },
        reverse: false,
    },
]

export const PremiumOverviewContainer: React.FC = () => {
    const { t } = useTranslation('translations')
    const sections = createSections(t)

    return (
        <div data-testid="premium-overview-container">
            <Header />
            {sections.map((section) => (
                <SectionBody
                    key={section.id}
                    className={section.reverse ? 'bg-white' : 'bg-neutral-50'}
                    section={section}
                />
            ))}
            <SectionFooter
                title={t('data_management.premiumOverview.footer.title')}
                subtitle={
                    <>
                        <span className="text-body-medium">
                            <FormattedI18nString
                                boldClassName="!font-normal"
                                i18nKey="data_management.premiumOverview.footer.contact"
                            />
                        </span>
                        <a
                            href="mailto:contact@myenergynetwork.com"
                            className="font-semibold text-blue-200"
                        >
                            {t('data_management.premiumOverview.footer.email')}
                        </a>
                        .
                    </>
                }
            />
        </div>
    )
}
